import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ToggleGroup, ToggleGroupItem } from '@ohif/ui-next';
import { Icons } from '@ohif/ui-next';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  displayedDisplaySetInstanceUIDs,
  servicesManager,
  viewPresets: initialViewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
}: withAppTypes) => {
  // Adicione o estado local para viewPresets
  const [viewPresets, setViewPresets] = useState(initialViewPresets);

  // Função para atualizar o preset selecionado
  const updateViewPresetValue = selectedPreset => {
    setViewPresets(prev =>
      prev.map(preset => ({
        ...preset,
        selected: preset.id === selectedPreset.id,
      }))
    );
  };

  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              displayedDisplaySetInstanceUIDs={displayedDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
            />
          </React.Fragment>
        );
      }
    );
  };

  return (
    <ScrollArea>
      <div
        className="flex min-w-0 flex-1 flex-col gap-[4px]"
        style={{ backgroundColor: '#2A2A2A' }}
        data-cy="studyBrowser-panel"
      >
        <div
          className="border-primary-dark relative flex gap-1 px-1 pt-2 pb-2"
          style={{ backgroundColor: '#2A2A2A' }}
        >
          {/* Background contínuo */}
          <div
            className="absolute inset-x-1 top-2 bottom-2 rounded"
            style={{ backgroundColor: '#1E1E1E' }}
          />

          {tabs.map(tab => (
            <button
              key={tab.name}
              onClick={() => onClickTab(tab.name)}
              className={`relative z-10 rounded px-1.5 py-1 text-[10px] font-medium transition-colors ${
                activeTabName === tab.name ? 'text-white' : 'text-gray-400 hover:text-white'
              }`}
              style={{
                backgroundColor: activeTabName === tab.name ? '#29AEAA' : '#1E1E1E',
                maxWidth: 'calc(100% - 10px)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="border-b border-gray-800 px-0 py-2 pl-2"
          style={{ backgroundColor: '#2A2A2A' }}
        >
          <div className="flex w-full items-center justify-between gap-0.5 text-[10px] text-white">
            <div className="flex min-w-0 items-center">
              <StudyBrowserSort servicesManager={servicesManager} />
            </div>
            <div className="pr-2">
              {' '}
              {/* Adiciona padding à direita */}
              <ToggleGroup
                type="single"
                value={viewPresets.find(preset => preset.selected)?.id}
                onValueChange={value => {
                  const selectedViewPreset = viewPresets.find(preset => preset.id === value);
                  if (selectedViewPreset) {
                    updateViewPresetValue(selectedViewPreset);
                  }
                }}
                className="ml-1 rounded-lg bg-[#1E1E1E]"
              >
                {viewPresets.map((viewPreset, index) => (
                  <ToggleGroupItem
                    key={index}
                    aria-label={viewPreset.id}
                    value={viewPreset.id}
                    className="px-1 py-0.5 text-white hover:text-white data-[state=on]:bg-[#29AEAA] data-[state=off]:bg-transparent"
                  >
                    <div className="flex h-5 w-5 items-center justify-center">
                      {React.createElement(Icons[viewPreset.iconName] || Icons.MissingIcon, {
                        className: 'w-8 h-8',
                      })}
                    </div>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden p-2">{getTabContent()}</div>
      </div>
    </ScrollArea>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              dragData: PropTypes.shape({
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
};

export { StudyBrowser };
